import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit2,
  Image,
  DollarSign,
  Tag,
  Layers,
  GripVertical,
  MoreVertical,
  Search,
  Upload,
  Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import ModernMenuEditor from '@/modern/admin/ModernMenuEditor';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function MenuEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image_url: '',
    sort_order: 0,
    is_active: true
  });
  
  const [productForm, setProductForm] = useState({
    category_id: '',
    name: '',
    description: '',
    price: 0,
    image_url: '',
    is_available: true,
    preparation_time: 15,
    variants: [],
    extras: [],
    tags: []
  });

  const [newVariant, setNewVariant] = useState({ name: '', price_modifier: 0 });
  const [newExtra, setNewExtra] = useState({ name: '', price: 0 });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true })
      ]);
      
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/categories/${editingCategory.category_id}`,
          categoryForm,
          { withCredentials: true }
        );
        setCategories(categories.map(c => 
          c.category_id === editingCategory.category_id 
            ? { ...c, ...categoryForm }
            : c
        ));
        toast.success('Categoría actualizada');
      } else {
        const response = await axios.post(`${API}/categories`, categoryForm, { withCredentials: true });
        setCategories([...categories, response.data]);
        toast.success('Categoría creada');
      }
      
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', image_url: '', sort_order: 0, is_active: true });
    } catch (error) {
      toast.error('Error al guardar categoría');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
      setCategories(categories.filter(c => c.category_id !== categoryId));
      toast.success('Categoría eliminada');
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  const handleSaveProduct = async () => {
    try {
      const productData = {
        ...productForm,
        variants: productForm.variants,
        extras: productForm.extras
      };

      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.product_id}`,
          productData,
          { withCredentials: true }
        );
        setProducts(products.map(p => 
          p.product_id === editingProduct.product_id 
            ? { ...p, ...productData }
            : p
        ));
        toast.success('Producto actualizado');
      } else {
        const response = await axios.post(`${API}/products`, productData, { withCredentials: true });
        setProducts([...products, response.data]);
        toast.success('Producto creado');
      }
      
      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({
        category_id: '',
        name: '',
        description: '',
        price: 0,
        image_url: '',
        is_available: true,
        preparation_time: 15,
        variants: [],
        extras: [],
        tags: []
      });
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      setProducts(products.filter(p => p.product_id !== productId));
      toast.success('Producto eliminado');
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      // Get the full URL for the uploaded image
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setProductForm({ ...productForm, image_url: imageUrl });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      sort_order: category.sort_order,
      is_active: category.is_active
    });
    setShowCategoryDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      category_id: product.category_id,
      name: product.name,
      description: product.description || '',
      price: product.price,
      image_url: product.image_url || '',
      is_available: product.is_available,
      preparation_time: product.preparation_time,
      variants: product.variants || [],
      extras: product.extras || [],
      tags: product.tags || []
    });
    setShowProductDialog(true);
  };

  const addVariant = () => {
    if (newVariant.name) {
      setProductForm({
        ...productForm,
        variants: [...productForm.variants, { ...newVariant }]
      });
      setNewVariant({ name: '', price_modifier: 0 });
    }
  };

  const removeVariant = (index) => {
    setProductForm({
      ...productForm,
      variants: productForm.variants.filter((_, i) => i !== index)
    });
  };

  const addExtra = () => {
    if (newExtra.name) {
      setProductForm({
        ...productForm,
        extras: [...productForm.extras, { ...newExtra }]
      });
      setNewExtra({ name: '', price: 0 });
    }
  };

  const removeExtra = (index) => {
    setProductForm({
      ...productForm,
      extras: productForm.extras.filter((_, i) => i !== index)
    });
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Editor de Menú</h1>
            <p className="text-sm text-muted-foreground">
              Gestiona categorías y productos
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            onClick={() => {
              setEditingProduct(null);
              setProductForm({
                category_id: selectedCategory || '',
                name: '',
                description: '',
                price: 0,
                image_url: '',
                is_available: true,
                preparation_time: 15,
                variants: [],
                extras: [],
                tags: []
              });
              setShowProductDialog(true);
            }}
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Categories Sidebar */}
        <div className="w-64 border-r border-border p-4 hidden md:block h-[calc(100vh-64px)] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Categorías</h3>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: '', description: '', image_url: '', sort_order: categories.length, is_active: true });
                setShowCategoryDialog(true);
              }}
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-2">
            <div
              className={`p-3 rounded-xl cursor-pointer transition-colors ${
                !selectedCategory ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setSelectedCategory(null)}
            >
              <p className="font-medium">Todos los productos</p>
              <p className="text-xs opacity-80">{products.length} items</p>
            </div>

            {categories.map((category) => (
              <div
                key={category.category_id}
                className={`p-3 rounded-xl cursor-pointer transition-colors group ${
                  selectedCategory === category.category_id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                onClick={() => setSelectedCategory(category.category_id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{category.name}</p>
                    <p className="text-xs opacity-80">
                      {products.filter(p => p.category_id === category.category_id).length} items
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.category_id); }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Products List */}
        <div className="flex-1 p-4 md:p-6">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-products"
            />
          </div>

          {/* Mobile Category Filter */}
          <div className="mb-4 md:hidden">
            <Select value={selectedCategory || 'all'} onValueChange={(v) => setSelectedCategory(v === 'all' ? null : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.category_id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No hay productos</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'No se encontraron productos con ese nombre' : 'Agrega tu primer producto al menú'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowProductDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Producto
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map((product) => {
                const category = categories.find(c => c.category_id === product.category_id);
                return (
                  <Card key={product.product_id} className="overflow-hidden">
                    {product.image_url && (
                      <div className="aspect-video bg-muted">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">{product.name}</h4>
                          {category && (
                            <Badge variant="secondary" className="text-xs">
                              {category.name}
                            </Badge>
                          )}
                        </div>
                        <p className="font-bold text-lg font-mono">
                          ${product.price.toFixed(2)}
                        </p>
                      </div>
                      
                      {product.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {product.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {!product.is_available && (
                            <Badge variant="destructive">Agotado</Badge>
                          )}
                          {product.variants?.length > 0 && (
                            <Badge variant="outline">{product.variants.length} variantes</Badge>
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteProduct(product.product_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Ej: Entradas, Bebidas, Postres"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                placeholder="Descripción de la categoría"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>URL de Imagen (opcional)</Label>
              <Input
                placeholder="https://..."
                value={categoryForm.image_url}
                onChange={(e) => setCategoryForm({ ...categoryForm, image_url: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Categoría activa</Label>
              <Switch
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCategory} data-testid="save-category-btn">
              {editingCategory ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  placeholder="Nombre del producto"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Categoría *</Label>
                <Select
                  value={productForm.category_id}
                  onValueChange={(value) => setProductForm({ ...productForm, category_id: value })}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                placeholder="Descripción del producto"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Precio *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                    className="pl-8"
                    data-testid="product-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tiempo de preparación (min)</Label>
                <Input
                  type="number"
                  value={productForm.preparation_time}
                  onChange={(e) => setProductForm({ ...productForm, preparation_time: parseInt(e.target.value) || 15 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Imagen del producto</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="URL de imagen o sube una..."
                  value={productForm.image_url}
                  onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                  className="flex-1"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {productForm.image_url && (
                <div className="mt-2 relative w-20 h-20 rounded-lg overflow-hidden border">
                  <img 
                    src={productForm.image_url} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Variants */}
            <div className="space-y-2">
              <Label>Variantes (tamaños, opciones)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Nombre (ej: Grande)"
                  value={newVariant.name}
                  onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="+/- precio"
                  value={newVariant.price_modifier}
                  onChange={(e) => setNewVariant({ ...newVariant, price_modifier: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
                <Button type="button" onClick={addVariant} variant="secondary">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {productForm.variants.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {productForm.variants.map((v, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {v.name} ({v.price_modifier >= 0 ? '+' : ''}{v.price_modifier})
                      <button onClick={() => removeVariant(i)} className="ml-1">×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Extras */}
            <div className="space-y-2">
              <Label>Extras (adicionales)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Nombre (ej: Extra queso)"
                  value={newExtra.name}
                  onChange={(e) => setNewExtra({ ...newExtra, name: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Precio"
                  value={newExtra.price}
                  onChange={(e) => setNewExtra({ ...newExtra, price: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
                <Button type="button" onClick={addExtra} variant="secondary">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {productForm.extras.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {productForm.extras.map((e, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {e.name} (+{e.price})
                      <button onClick={() => removeExtra(i)} className="ml-1">×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label>Producto disponible</Label>
              <Switch
                checked={productForm.is_available}
                onCheckedChange={(checked) => setProductForm({ ...productForm, is_available: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveProduct} data-testid="save-product-btn">
              {editingProduct ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MenuEditorPage() {
  return useIsModern() ? <ModernMenuEditor /> : <MenuEditor />;
}
